// React Dependencies
import React, { useState, forwardRef, useEffect } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { Skeleton } from '@material-ui/lab';

// Internal Dependencies
import colours from '../styles/colours';
import { IsEmpty } from '../common/checks';

const ProgressiveImage = forwardRef(({
  image, alt, width, height, style, onLoad, LoadingComponent,
}, ref) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [comImage, setComImage] = useState(null);

  useEffect(() => {
    // To avoid UI stucked at showing skeleton on UI refresh,
    // we need to check if there is an actual change in the image
    // before we show skeleton for new image to load.
    if (IsEmpty(image) || image !== comImage) {
      setIsLoaded(false);
      setComImage(image);
    }
  }, [image]);

  return (
    <div
      style={{ position: 'relative', ...style }}
    >
      <img
        ref={ref}
        alt={alt}
        src={comImage}
        onLoad={(e) => {
          setIsLoaded(true);
          onLoad(e);
        }}
        onError={() => setIsLoaded(false)}
        style={{ width, height, backgroundColor: 'transparent', ...style }}
      />

      <div style={{
        width,
        // Height cannot be 'auto' else skeleton doesn't show up
        height:   (height && height !== 'auto') ? height : '100%',
        zIndex:   style?.zIndex ? style.zIndex + 1 : 5,
        position: 'absolute',
        top:      0,
        left:     0,
        right:    0,
      }}
      >
        {
          !isLoaded
          && (LoadingComponent
            ? <LoadingComponent />
            : (
              <Skeleton
                animation="wave"
                variant="rect"
                width={width}
                // Height cannot be 'auto' else skeleton doesn't show up
                height={(height && height !== 'auto') ? height : '100%'}
                style={
                  ({ position: 'relative', backgroundColor: colours.LightGray,
                    ...(style?.borderTopLeftRadius ? { borderTopLeftRadius: style.borderTopLeftRadius } : {}),
                    ...(style?.borderTopRightRadius ? { borderTopRightRadius: style.borderTopRightRadius } : {}),
                    ...(style?.borderBottomLeftRadius ? { borderBottomLeftRadius: style.borderBottomLeftRadius } : {}),
                    ...(style?.borderBottomRightRadius ? { borderBottomRightRadius: style.borderBottomRightRadius } : {}),
                    ...(style?.borderRadius ? { borderRadius: style.borderRadius } : {}),
                  })
                }
              />
            )
          )
        }
      </div>
    </div>
  );
});

ProgressiveImage.displayName = 'ProgressiveImage';

ProgressiveImage.propTypes = {
  style:            PropTypes.shape(Object),
  alt:              PropTypes.string,
  image:            PropTypes.string.isRequired,
  width:            PropTypes.string,
  height:           PropTypes.string,
  onLoad:           PropTypes.func,
  LoadingComponent: PropTypes.func,
};

ProgressiveImage.defaultProps = {
  style:            {},
  alt:              'Image',
  width:            '100%',
  height:           '200px',
  onLoad:           () => {},
  LoadingComponent: null,
};

export default ProgressiveImage;
